// Phase C section-content editors for ProposalDisplayCard — real popups (matching the approved
// mockup's own click-to-open-modal pattern) for every section whose content is a genuine user
// SELECTION rather than something purely computed/derived: which property photos to include,
// which tenant payment term/exclusions/clauses/previous-work case studies apply to THIS proposal,
// and ad hoc warranty terms (no warranty_master entity exists yet, so this is free-form entry
// rather than a picker over a master list).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/UI/Modal';
import { useGetPropertyMediaQuery, type PropertyMedia } from '@/redux/internal-services/workspaceApiExtensions';
import {
  useListPaymentTermsQuery, useListExclusionsQuery, useListTermClausesQuery,
  useListMarketingReferencesQuery, type PaymentTerm, type ExclusionItem, type TermClause,
  type MarketingReference,
} from '@/redux/internal-services/proposalsApi';

interface SavableModalProps {
  onClose: () => void;
  saving: boolean;
}

// ── Photo picker ─────────────────────────────────────────────────────────────────────

export interface SelectedPhoto {
  media_id: string;
  s3_url: string;
  caption?: string | null;
}

export function PhotoPickerModal({
  propertyId, initialMediaIds, onSave, onClose, saving,
}: SavableModalProps & { propertyId: string; initialMediaIds: string[]; onSave: (photos: SelectedPhoto[]) => void }) {
  const { t } = useTranslation();
  const { data: media = [], isLoading } = useGetPropertyMediaQuery(propertyId, { skip: !propertyId });
  const photos = media.filter((m) => m.media_type === 'photo');
  const [selected, setSelected] = useState<string[]>(initialMediaIds);

  const toggle = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Modal
      title={t('workflow.proposalSelectPhotos', 'Select photos')}
      onClose={onClose}
      saving={saving}
      onSave={() => onSave(selected.map((id) => {
        const m = photos.find((p) => p.media_id === id) as PropertyMedia;
        return { media_id: id, s3_url: m?.s3_url, caption: m?.caption };
      }))}
      maxWidthClassName="max-w-2xl"
      footerLeft={<span>{t('workflow.proposalPhotosSelectedCount', '{{count}} selected', { count: selected.length })}</span>}
    >
      {isLoading && <p className="text-xs text-ui-text3">{t('workflow.loading', 'Loading…')}</p>}
      {!isLoading && photos.length === 0 && (
        <p className="text-xs text-ui-text3">{t('workflow.proposalNoPropertyPhotos', 'No photos on this property yet.')}</p>
      )}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {photos.map((m) => {
          const isSelected = selected.includes(m.media_id);
          return (
            <button
              key={m.media_id} type="button" onClick={() => toggle(m.media_id)}
              className={`relative aspect-square overflow-hidden rounded-lg border-2 ${isSelected ? 'border-accent' : 'border-transparent'}`}
            >
              <img src={m.s3_url} alt={m.caption || m.filename || ''} className="h-full w-full object-cover" />
              {isSelected && (
                <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent-fill text-xs font-bold text-on-accent">✓</span>
              )}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

// ── Generic checkbox multi-select (Exclusions / Terms / Previous Work) ─────────────────

function MultiSelectPickerModal<T>({
  title, items, isLoading, idOf, labelOf, subLabelOf, initialSelectedIds, onSave, onClose, saving, emptyLabel,
}: SavableModalProps & {
  title: string;
  items: T[];
  isLoading: boolean;
  idOf: (item: T) => string;
  labelOf: (item: T) => string;
  subLabelOf?: (item: T) => string | null | undefined;
  initialSelectedIds: string[];
  onSave: (selected: T[]) => void;
  emptyLabel: string;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>(initialSelectedIds);
  const toggle = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Modal
      title={title} onClose={onClose} saving={saving}
      onSave={() => onSave(items.filter((i) => selected.includes(idOf(i))))}
    >
      {isLoading && <p className="text-xs text-ui-text3">{t('workflow.loading', 'Loading…')}</p>}
      {!isLoading && items.length === 0 && <p className="text-xs text-ui-text3">{emptyLabel}</p>}
      <div className="space-y-1">
        {items.map((item) => {
          const id = idOf(item);
          const sub = subLabelOf?.(item);
          return (
            <label key={id} className="flex cursor-pointer items-start gap-2 rounded-lg px-1.5 py-1.5 hover:bg-ui-hover">
              <input type="checkbox" className="mt-0.5" checked={selected.includes(id)} onChange={() => toggle(id)} />
              <span className="text-xs text-ui-text">
                {labelOf(item)}
                {sub && <span className="mt-0.5 block text-xs text-ui-text3">{sub}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </Modal>
  );
}

export function ExclusionsPickerModal(
  props: SavableModalProps & { initialCodes: string[]; onSave: (items: ExclusionItem[]) => void },
) {
  const { t } = useTranslation();
  const { data = [], isLoading } = useListExclusionsQuery();
  return (
    <MultiSelectPickerModal
      title={t('workflow.proposalSelectExclusions', 'Select exclusions')}
      items={data} isLoading={isLoading}
      idOf={(i) => i.exclusion_code} labelOf={(i) => i.text}
      initialSelectedIds={props.initialCodes} onSave={props.onSave} onClose={props.onClose} saving={props.saving}
      emptyLabel={t('workflow.proposalNoExclusionsConfigured', 'No exclusions configured for this tenant yet.')}
    />
  );
}

export function TermClausesPickerModal(
  props: SavableModalProps & { initialCodes: string[]; onSave: (items: TermClause[]) => void },
) {
  const { t } = useTranslation();
  const { data = [], isLoading } = useListTermClausesQuery();
  return (
    <MultiSelectPickerModal
      title={t('workflow.proposalSelectClauses', 'Select terms & conditions')}
      items={data} isLoading={isLoading}
      idOf={(i) => i.clause_code} labelOf={(i) => i.text} subLabelOf={(i) => i.category}
      initialSelectedIds={props.initialCodes} onSave={props.onSave} onClose={props.onClose} saving={props.saving}
      emptyLabel={t('workflow.proposalNoClausesConfigured', 'No clauses configured for this tenant yet.')}
    />
  );
}

export function PreviousWorkPickerModal(
  props: SavableModalProps & { initialIds: string[]; onSave: (items: MarketingReference[]) => void },
) {
  const { t } = useTranslation();
  const { data = [], isLoading } = useListMarketingReferencesQuery();
  return (
    <MultiSelectPickerModal
      title={t('workflow.proposalSelectPreviousWork', 'Select previous work references')}
      items={data} isLoading={isLoading}
      idOf={(i) => i.reference_id} labelOf={(i) => i.title} subLabelOf={(i) => i.location}
      initialSelectedIds={props.initialIds} onSave={props.onSave} onClose={props.onClose} saving={props.saving}
      emptyLabel={t('workflow.proposalNoReferencesConfigured', 'No previous-work references added for this tenant yet.')}
    />
  );
}

// ── Payment term (single-select) ────────────────────────────────────────────────────

export function PaymentTermPickerModal(
  props: SavableModalProps & { initialCode: string | null; onSave: (term: PaymentTerm) => void },
) {
  const { t } = useTranslation();
  const { data = [], isLoading } = useListPaymentTermsQuery();
  const [selectedCode, setSelectedCode] = useState<string | null>(props.initialCode);
  const selected = data.find((term) => term.payment_term_code === selectedCode);

  return (
    <Modal
      title={t('workflow.proposalSelectPaymentTerm', 'Select payment terms')}
      onClose={props.onClose} saving={props.saving}
      onSave={() => selected && props.onSave(selected)}
      saveDisabled={!selected}
    >
      {isLoading && <p className="text-xs text-ui-text3">{t('workflow.loading', 'Loading…')}</p>}
      {!isLoading && data.length === 0 && (
        <p className="text-xs text-ui-text3">{t('workflow.proposalNoPaymentTermsConfigured', 'No payment terms configured for this tenant yet.')}</p>
      )}
      <div className="space-y-1">
        {data.map((term) => (
          <label key={term.payment_term_code} className="flex cursor-pointer items-start gap-2 rounded-lg px-1.5 py-1.5 hover:bg-ui-hover">
            <input type="radio" className="mt-0.5" name="payment-term" checked={selectedCode === term.payment_term_code} onChange={() => setSelectedCode(term.payment_term_code)} />
            <span className="text-xs text-ui-text">
              <span className="font-medium">{term.name}</span>{term.default ? ` · ${t('workflow.proposalDefault', 'default')}` : ''}
              <span className="mt-0.5 block text-xs text-ui-text3">
                {term.payment_schedule.map((m) => `${m.milestone} — ${m.percentage}%`).join(' · ')}
              </span>
            </span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

// ── Warranty (ad hoc, no master list exists) ────────────────────────────────────────

export interface WarrantyEntry {
  title: string;
  duration: string;
  description?: string | null;
}

export function WarrantyEditorModal(
  props: SavableModalProps & { initial: WarrantyEntry[]; onSave: (warranties: WarrantyEntry[]) => void },
) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<WarrantyEntry[]>(props.initial.length ? props.initial : []);

  const update = (idx: number, patch: Partial<WarrantyEntry>) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const remove = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const add = () => setRows((prev) => [...prev, { title: '', duration: '' }]);

  return (
    <Modal
      title={t('workflow.proposalEditWarranty', 'Warranty')}
      onClose={props.onClose} saving={props.saving}
      onSave={() => props.onSave(rows.filter((r) => r.title.trim() || r.duration.trim()))}
      maxWidthClassName="max-w-xl"
    >
      <div className="space-y-3">
        {rows.map((row, idx) => (
          <div key={idx} className="rounded-lg border border-ui-border p-2.5">
            <div className="flex gap-2">
              <input
                value={row.title} onChange={(e) => update(idx, { title: e.target.value })}
                placeholder={t('workflow.proposalWarrantyTitlePlaceholder', 'e.g. Manufacturer Materials Warranty')}
                className="flex-1 rounded-md border border-ui-border bg-ui-card px-2 py-1.5 text-xs"
              />
              <input
                value={row.duration} onChange={(e) => update(idx, { duration: e.target.value })}
                placeholder={t('workflow.proposalWarrantyDurationPlaceholder', 'e.g. 25 years')}
                className="w-28 rounded-md border border-ui-border bg-ui-card px-2 py-1.5 text-xs"
              />
              <button type="button" onClick={() => remove(idx)} className="px-1 text-xs text-ui-text3 hover:text-danger-text">✕</button>
            </div>
            <textarea
              value={row.description ?? ''} onChange={(e) => update(idx, { description: e.target.value })}
              placeholder={t('workflow.proposalWarrantyDescPlaceholder', 'Optional details')}
              rows={2}
              className="mt-1.5 w-full resize-none rounded-md border border-ui-border bg-ui-card px-2 py-1.5 text-xs"
            />
          </div>
        ))}
        <button type="button" onClick={add} className="rounded-md border border-dashed border-ui-border px-2.5 py-1.5 text-xs font-medium text-ui-text2 hover:bg-ui-hover">
          + {t('workflow.proposalAddWarranty', 'Add warranty')}
        </button>
      </div>
    </Modal>
  );
}
